import React, { useEffect, useState } from "react";
import { Modal, Button, DatePicker, Space, message } from "antd";
import styled from "styled-components";
import moment from "moment";
import { BRAND, lastDays } from "./utils";

const WEEKDAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 12px;
  color: #888;
  padding-bottom: 4px;
`;

const Day = styled.button`
  height: 38px;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$selected ? BRAND : "#e5e5e5")};
  background: ${(p) => (p.$selected ? BRAND : "#fff")};
  color: ${(p) => (p.$selected ? "#fff" : p.$today ? BRAND : "#333")};
  font-weight: ${(p) => (p.$today || p.$selected ? 600 : 400)};
  cursor: pointer;
  &:hover {
    border-color: ${BRAND};
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 10px;
`;

/**
 * Birden fazla, ardışık olmayan gün seçimi. Ay değiştirmek seçimi silmez;
 * farklı aylardan günler birlikte seçilebilir.
 */
const DaySelectModal = ({ open, value, onCancel, onApply }) => {
  const [pending, setPending] = useState(new Set(value));
  const [month, setMonth] = useState(moment().startOf("month"));
  const [range, setRange] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPending(new Set(value));
    const last = value.length ? moment(value[value.length - 1]) : moment();
    setMonth(last.clone().startOf("month"));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (day) => {
    const next = new Set(pending);
    next.has(day) ? next.delete(day) : next.add(day);
    setPending(next);
  };

  const quick = (n) => {
    setPending(new Set(lastDays(n)));
    setMonth(moment().startOf("month"));
  };

  const addRange = () => {
    if (!range || !range[0] || !range[1]) {
      message.warning("Başlangıç ve bitiş tarihlerini seçin.");
      return;
    }
    const next = new Set(pending);
    const d = range[0].clone().startOf("day");
    for (let i = 0; d.isSameOrBefore(range[1], "day") && i < 366; i++) {
      next.add(d.format("YYYY-MM-DD"));
      d.add(1, "day");
    }
    setPending(next);
    setRange(null);
  };

  const today = moment().format("YYYY-MM-DD");
  const offset = (month.isoWeekday() + 6) % 7; // Pazartesi ile başlayan hafta
  const cells = [
    ...Array.from({ length: offset }, () => null),
    ...Array.from({ length: month.daysInMonth() }, (_, i) => month.clone().date(i + 1).format("YYYY-MM-DD")),
  ];

  return (
    <Modal
      title="Gün seçimi"
      visible={open}
      onCancel={onCancel}
      width={480}
      footer={[
        <span key="sum" style={{ float: "left", lineHeight: "32px", color: "#666" }}>
          {pending.size} gün seçili
        </span>,
        <Button key="cancel" onClick={onCancel}>Vazgeç</Button>,
        <Button
          key="apply"
          type="primary"
          disabled={!pending.size}
          onClick={() => onApply([...pending].sort())}
        >
          Seçimi Uygula
        </Button>,
      ]}
    >
      <p style={{ color: "#666", marginTop: 0 }}>
        Birden fazla günü seçin. Farklı aylardan günler birlikte seçilebilir.
      </p>
      <Space wrap>
        <Button size="small" onClick={() => quick(7)}>Son 7 gün</Button>
        <Button size="small" onClick={() => quick(30)}>Son 30 gün</Button>
        <Button size="small" onClick={() => setPending(new Set())}>Temizle</Button>
      </Space>

      <Head>
        <Button size="small" onClick={() => setMonth(month.clone().subtract(1, "month"))}>‹</Button>
        <b>{month.clone().locale("tr").format("MMMM YYYY")}</b>
        <Button size="small" onClick={() => setMonth(month.clone().add(1, "month"))}>›</Button>
      </Head>
      <Grid>
        {WEEKDAYS.map((w) => <WeekDay key={w}>{w}</WeekDay>)}
        {cells.map((day, i) =>
          day ? (
            <Day
              key={day}
              type="button"
              $selected={pending.has(day)}
              $today={day === today}
              onClick={() => toggle(day)}
            >
              {Number(day.slice(8))}
            </Day>
          ) : (
            <span key={"e" + i} />
          )
        )}
      </Grid>

      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Tarih aralığını topluca ekle</div>
        <Space wrap>
          <DatePicker.RangePicker value={range} onChange={setRange} format="DD.MM.YYYY" />
          <Button onClick={addRange}>Aralığı ekle</Button>
        </Space>
      </div>
    </Modal>
  );
};

export default DaySelectModal;
